import { AbsoluteHref } from '@weenat/client/dist/routx/runtime-core'
import { isSpacingToken } from '@weenat/theme'
import isEmpty from 'lodash-es/isEmpty'
import isNil from 'lodash-es/isNil'
import isNumber from 'lodash-es/isNumber'
import { CSSProperties, ReactNode } from 'react'
import { css, styled } from 'styled-components'
import Link from './LinkComponent'
import ListEmpty, { ListEmptyProps } from './ListEmpty'
import LoadingTable from './LoadingTable'
import { FlexProps } from './primitives/themeMappings/props'

const ROW_HEIGHT = 32
const SMALL_SCREEN_BREAKPOINT = 1024

interface ExtraTableCellProps {
  $fontWeight?: number
  $hideOnSmallScreen?: boolean
  $ellipsis?: boolean
  $width: number
  $fontVariantNumeric?: CSSProperties['fontVariantNumeric']
}

export const TableCell = styled.div<Partial<ExtraTableCellProps>>`
  padding: 8px;
  width: ${(p) => (!isNil(p.$width) ? `${p.$width}px` : '100%')};
  min-height: ${ROW_HEIGHT}px;
  color: ${({ theme }) => theme.colors.grayscale.black};
  line-height: ${({ theme }) => theme.typography.lineHeights.md};
  font-weight: ${({ $fontWeight }) => (!isNil($fontWeight) ? $fontWeight : 300)};
  font-size: ${(p) => p.theme.typography.sizes.rg}px;
  overflow-wrap: break-word;

  ${({ $fontVariantNumeric }) =>
    !isNil($fontVariantNumeric)
      ? css`
          font-variant-numeric: ${$fontVariantNumeric};
        `
      : ''}

  ${({ $hideOnSmallScreen }) =>
    $hideOnSmallScreen &&
    css`
      @media (max-width: ${SMALL_SCREEN_BREAKPOINT}px) {
        display: none;
      }
    `}

  ${({ $ellipsis }) =>
    $ellipsis &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `};
`

interface TableRowProps {
  $gridTemplateColumns: string
  $clickable?: boolean
  $hasClickableItemInRow?: boolean
  $p: FlexProps['$p']
}

/**
 * The fact that all rows are Link is not optimal
 * We should be more composable when declaring a table
 */
const TableRow = styled(Link)<TableRowProps>`
  all: unset;
  position: relative;

  display: grid;
  grid-template-columns: ${(p) => p.$gridTemplateColumns};
  grid-template-rows: repeat(1, 1fr);
  grid-gap: 4px;

  padding: ${(p) =>
    isNumber(p.$p) ? `${p.$p}px` : isSpacingToken(p.$p) ? `${p.theme.spacings[p.$p]}px` : p.$p};
  border-bottom: 1px solid ${(p) => p.theme.colors.grayscale[100]};

  flex: none;

  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  pointer-events: ${(p) => (p.$clickable || p.$hasClickableItemInRow ? 'auto' : 'none')};

  ${(p) =>
    p.$clickable &&
    css`
      &:hover {
        background-color: ${p.theme.colors.primary[200]} !important;
      }
    `}
`

const TableHeader = styled(TableRow)<{ $sticky: boolean }>`
  /*
   * Currently the header is only sticky in one case : ForecastsTable
   * so the position values are only fitted for that usage 
   * But if a new case should present itself we should refactor Table to be more composable so that it's easier to access and modify all the different components
   */
  ${(p) =>
    p.$sticky
      ? css`
          position: sticky;
          left: 0px;
          top: 292px;
          z-index: 2;
          background-color: ${(props) => props.theme.colors.grayscale.white};
        `
      : null}

  border-color: ${(props) => props.theme.colors.grayscale[300]};
  color: ${(props) => props.theme.colors.grayscale[700]};
  font-weight: 400;
  line-height: 1;
`

const TableHeaderCell = styled(TableCell)`
  color: ${(props) => props.theme.colors.grayscale[700]};
  font-weight: ${(props) => props.theme.typography.weights.medium};
  font-size: ${(props) => props.theme.typography.sizes.sm}px;
  line-height: 1;
`

const StyledTable = styled(Flex)`
  height: 100%;
  width: 100%;
  flex-direction: column;
`

const TableHolder = styled(Flex)<{ $outlined?: boolean }>`
  flex-direction: column;
  flex: 1;

  ${(props) =>
    props.$outlined &&
    css`
      border-radius: ${props.theme.radiuses.md}px;
      border: 1px solid ${props.theme.colors.grayscale[300]};
    `}

  width: 100%;
  margin: ${(p) => `${p.theme.spacings.md}px 0px`};

  overflow: hidden;
`

const TableBody = styled(Flex)<{ $maxHeight?: CSSProperties['maxHeight'] }>`
  flex-direction: column;
  flex: 1;
  overflow-y: auto;

  ${(props) =>
    !isNil(props.$maxHeight) &&
    css`
      max-height: ${props.$maxHeight};
    `}

  /** Hiding scrollbar but keeping scroll */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  /** Chromium based */
  &::-webkit-scrollbar {
    display: none;
  }
`

export interface TableColumn {
  label: string
  /** use a number for fr a string for specific eg "12px" */
  width: number | string
  hideOnSmallScreen?: boolean
}

export interface TableProps<T> {
  data?: T[]
  getLinkFromItem?: (item?: T) => AbsoluteHref
  hasClickableItemInRow?: boolean
  renderRow: (item: T, index?: number) => ReactNode
  keyExtractor: (item: T) => string | number
  columns: TableColumn[]
  emptyMessage?: string
  isLoading?: boolean
  tableName: string
  rowPadding?: FlexProps['$p']
  /** Whether or not to add a border around the table */
  outlined?: boolean
  model?: ListEmptyProps['model']
  /** Whether or not the search query is empty */
  isSearchEmpty?: boolean
  stickyFooter?: ReactNode
  maxHeight?: CSSProperties['maxHeight']
  /** Whether or not the table header should be sticky */
  stickHeader?: boolean
  className?: string
}

function Table<T>({
  data = [],
  getLinkFromItem,
  hasClickableItemInRow = false,
  renderRow,
  keyExtractor,
  columns = [],
  emptyMessage,
  rowPadding = 4,
  isLoading = false,
  tableName,
  outlined = false,
  model,
  isSearchEmpty = false,
  stickyFooter = null,
  maxHeight,
  className,
  stickHeader = false
}: TableProps<T>) {
  const gridTemplateColumns = columns
    .map(({ width }) => (isNumber(width) ? `minmax(0, ${width}fr)` : `minmax(0, ${width})`))
    .join(' ')
  const isTableEmpty = isEmpty(data)

  return (
    <TableHolder className={className} $outlined={outlined}>
      <StyledTable>
        {/* Headers */}
        {!isEmpty(columns) ? (
          <TableHeader $sticky={stickHeader} $gridTemplateColumns={gridTemplateColumns}>
            {columns.map(({ label, hideOnSmallScreen }, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableHeaderCell $hideOnSmallScreen={hideOnSmallScreen} key={`${label}-${index}`}>
                {label}
              </TableHeaderCell>
            ))}
          </TableHeader>
        ) : null}
        {isLoading ? (
          <LoadingTable
            uniqueKey={`${tableName}-loader`}
            numberOfColumns={!isEmpty(columns) ? columns.length : 6}
            numberOfRows={3}
          />
        ) : isTableEmpty ? (
          <ListEmpty
            message={emptyMessage}
            model={model}
            mood={isSearchEmpty ? 'confused' : 'sad'}
          />
        ) : (
          <TableBody $maxHeight={maxHeight}>
            {data.map((item, idx) => {
              const to = getLinkFromItem?.(item)

              return (
                <TableRow
                  key={keyExtractor(item)}
                  $gridTemplateColumns={gridTemplateColumns}
                  $clickable={!isNil(to)}
                  to={to}
                  $p={rowPadding}
                  $hasClickableItemInRow={hasClickableItemInRow}
                >
                  {renderRow(item, idx)}
                </TableRow>
              )
            })}
          </TableBody>
        )}
        {stickyFooter}
      </StyledTable>
    </TableHolder>
  )
}

export default Table
